import { useParams, useNavigate } from "react-router-dom";

export const SUPPORTED_LOCALES = ["en", "es"] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const DEFAULT_LOCALE: Locale = "en";

export const isValidLocale = (locale: string | undefined): locale is Locale => {
  return SUPPORTED_LOCALES.includes(locale as Locale);
};

/**
 * Gets the stored locale from localStorage, checking both lnc-i18n and i18nextLng
 * Prioritizes lnc-i18n if it exists, otherwise falls back to i18nextLng
 * Includes validation to ensure only valid locales are returned
 */
export const getStoredLocaleFromStorage = (): Locale => {
  // Check lnc-i18n first (priority)
  const lincLocale = localStorage.getItem("lnc-i18n");
  const validatedLincLocale = validateLocale(lincLocale);
  if (validatedLincLocale) {
    return validatedLincLocale;
  }
  
  // Fall back to i18nextLng
  const i18nextLocale = localStorage.getItem("i18nextLng");
  const validatedI18nextLocale = validateLocale(i18nextLocale);
  if (validatedI18nextLocale) {
    return validatedI18nextLocale;
  }
  
  // If invalid values found, clean them up
  if (lincLocale && !validatedLincLocale) {
    console.warn(`Invalid locale value in lnc-i18n: ${lincLocale}. Removing invalid value.`);
    localStorage.removeItem("lnc-i18n");
  }
  if (i18nextLocale && !validatedI18nextLocale) {
    console.warn(`Invalid locale value in i18nextLng: ${i18nextLocale}. Removing invalid value.`);
    localStorage.removeItem("i18nextLng");
  }
  
  return DEFAULT_LOCALE;
};

export const getLocalizedPath = (path: string, locale: Locale): string => {
  // Remove leading slash if present
  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  // Remove locale prefix if already present
  const pathWithoutLocale = cleanPath.replace(/^(en|es)\//, "");
  return `/${locale}/${pathWithoutLocale}`;
};

/** App basename (from index.tsx BrowserRouter) */

/**
 * Extracts locale from URL pathname
 * Handles paths like /user/es/login or /es/login
 * Returns the locale if valid, otherwise null
 */
export const getLocaleFromUrl = (pathname: string): Locale | null => {
  // Remove /user/ prefix if present
  const withoutBase = pathname.replace(/^\/user\//, "");
  const pathSegments = withoutBase.split("/").filter(Boolean);
  
  if (pathSegments.length > 0 && isValidLocale(pathSegments[0])) {
    return pathSegments[0] as Locale;
  }
  
  return null;
};

export const getPathWithoutLocale = (pathname: string): string => {
  // Remove locale prefix (pathname is already without /user/ due to basename)
  // Handle both cases: with or without /user/ prefix for flexibility
  const withoutBase = pathname.replace(/^\/user\//, "");
  const pathSegments = withoutBase.split("/").filter(Boolean);
  // Remove locale if it's the first segment
  const withoutLocale =
    pathSegments.length > 0 && isValidLocale(pathSegments[0])
      ? pathSegments.slice(1).join("/")
      : pathSegments.join("/");
  return withoutLocale || "login";
};

/**
 * Gets the active locale with priority: URL > localStorage > default
 * This is the main function to determine which locale should be used
 */
export const getActiveLocale = (pathname: string): Locale => {
  // Priority 1: URL locale (highest priority)
  const urlLocale = getLocaleFromUrl(pathname);
  if (urlLocale) {
    return urlLocale;
  }
  
  // Priority 2: localStorage (lnc-i18n or i18nextLng)
  const storedLocale = getStoredLocaleFromStorage();
  if (storedLocale) {
    return storedLocale;
  }
  
  // Priority 3: Default locale
  return DEFAULT_LOCALE;
};

export const useLocale = (): Locale => {
  const { locale } = useParams<{ locale: string }>();
  
  // If URL has valid locale, use it (highest priority)
  if (isValidLocale(locale)) {
    return locale;
  }
  
  // Otherwise, check localStorage for stored locale (checks both keys)
  return getStoredLocaleFromStorage();
};

export const useLocalizedNavigate = () => {
  const navigate = useNavigate();
  const locale = useLocale();

  // All internal navigation within this module uses replace:true so that
  // module-internal routes never accumulate in the browser history stack.
  // Pressing the browser back button will skip past all module pages and
  // return the user to wherever they came from (home / another MFE).
  return (path: string, options?: { replace?: boolean; state?: unknown }) => {
    const localizedPath = getLocalizedPath(path, locale);
    navigate(localizedPath, { replace: true, ...options });
  };
};

/**
 * Validates and sanitizes locale value
 * Returns valid locale or null
 */
export const validateLocale = (locale: string | null | undefined): Locale | null => {
  if (!locale) {
    return null;
  }
  
  // Trim whitespace and convert to lowercase for validation
  const normalized = locale.trim().toLowerCase();
  
  if (isValidLocale(normalized)) {
    return normalized as Locale;
  }
  
  return null;
};

/**
 * Sets the language in both localStorage keys (i18nextLng and lnc-i18n)
 * This ensures consistency across the application
 * Includes validation to ensure only valid locales are stored
 */
export const setLanguageInStorage = (locale: Locale): void => {
  // Validate locale before storing
  const validatedLocale = validateLocale(locale);
  if (!validatedLocale) {
    console.warn(`Attempted to set invalid locale: ${locale}. Supported locales: ${SUPPORTED_LOCALES.join(", ")}`);
    return;
  }
  
  // Store in both keys for consistency
  localStorage.setItem("i18nextLng", validatedLocale);
  localStorage.setItem("lnc-i18n", validatedLocale);
};

/**
 * Monitors localStorage for external changes to lnc-i18n (e.g., from repo or other sources)
 * Validates and syncs the value when changed externally
 */
export const setupLocaleStorageListener = (onLocaleChange: (locale: Locale) => void): (() => void) => {
  const handleStorageChange = (e: StorageEvent) => {
    // Only handle changes to lnc-i18n or i18nextLng
    if (e.key === "lnc-i18n" || e.key === "i18nextLng") {
      const newValue = e.newValue;
      const validatedLocale = validateLocale(newValue);
      
      if (validatedLocale) {
        // Sync both keys
        setLanguageInStorage(validatedLocale);
        // Notify listener
        onLocaleChange(validatedLocale);
      } else if (newValue !== null) {
        // Invalid value was set - restore to default
        console.warn(`Invalid locale value detected in localStorage: ${newValue}. Resetting to default.`);
        setLanguageInStorage(DEFAULT_LOCALE);
        onLocaleChange(DEFAULT_LOCALE);
      }
    }
  };
  
  window.addEventListener("storage", handleStorageChange);
  
  // Return cleanup function
  return () => {
    window.removeEventListener("storage", handleStorageChange);
  };
};